// src/pages/SelectRegularPaperedit.jsx
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/selectregularpaper.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';

const PAPER_FORM_KEY = 'regularPaperForm';
const apiBase = 'https://digital.tifleducation.com/admin/public/api';

const SelectRegularPaperedit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const paperId = state.paperId || null;

  // Board/Grade/Subject IDs
  const storedBoard = JSON.parse(localStorage.getItem('selectedBoard') || 'null');
  const storedGrade = JSON.parse(localStorage.getItem('selectedGrade') || 'null');
  const storedSubject = JSON.parse(localStorage.getItem('selectedSubject') || 'null');

  const boardId = state.boardId || storedBoard?.id;
  const gradeId = state.gradeId || storedGrade?.id;
  const subjectId = state.subjectId || storedSubject?.id;

  // Save Board/Grade/Subject in localStorage
  useEffect(() => {
    if (boardId) localStorage.setItem('selectedBoardId', boardId);
    if (gradeId) localStorage.setItem('selectedGradeId', gradeId);
    if (subjectId) localStorage.setItem('selectedSubjectId', subjectId);
  }, [boardId, gradeId, subjectId]);

  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Form data for counts
  const [formData, setFormData] = useState({
    mcq: '',
    fill: '',
    truefalse: '',
    veryshort: '',
    short: '',
    long: '',
    matching: '',
    diagram: '',
  });

  // Selected questions (chapter_wise_options)
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  // -------------------------
  // Initialize formData & selectedQuestions
  // -------------------------
  useEffect(() => {
    const initForm = async () => {
      // 1️⃣ Use state.counts if passed via navigation
      if (state.counts) {
        setFormData(state.counts);
        localStorage.setItem(PAPER_FORM_KEY, JSON.stringify(state.counts));
      }

      // 2️⃣ Use localStorage if no state.counts
      const savedForm = JSON.parse(localStorage.getItem(PAPER_FORM_KEY) || 'null');
      if (savedForm && !state.counts) {
        setFormData(savedForm);
      }

      // 3️⃣ Fetch from DB if paperId exists
      if (paperId) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${apiBase}/papers/${paperId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const paper = res.data?.data?.paper || {};

          // Map DB keys to formData keys → always convert arrays to counts
          const keyMap = {
            mcq: 'mcq',
            fill_in_the_blanks: 'fill',
            true_false: 'truefalse',
            very_short_qa: 'veryshort',
            short_qa: 'short',
            long_qa: 'long',
            matching_type: 'matching',
            diagram_image_based: 'diagram',
          };

          const mappedCounts = {};
          Object.entries(keyMap).forEach(([dbKey, formKey]) => {
            const val = paper[dbKey];
            // ✅ if array → use length, else number or 0
            mappedCounts[formKey] = Array.isArray(val) ? val.length : Number(val || 0);
          });

          // Only update formData if no local state or state.counts exist
          if (!state.counts && !savedForm) {
            setFormData(mappedCounts);
            localStorage.setItem(PAPER_FORM_KEY, JSON.stringify(mappedCounts));
          }

          // Set selected questions
          setSelectedQuestions(paper.chapter_wise_options || []);

        } catch (error) {
          console.error('Error fetching paper data:', error.response?.data || error);
          toast.error('Failed to load paper counts from database.');
        }
      }
    };

    initForm();
  }, [paperId, state.counts]);

  // Save formData to localStorage on change
  useEffect(() => {
    localStorage.setItem(PAPER_FORM_KEY, JSON.stringify(formData));
  }, [formData]);

  // Redirect if Board/Grade/Subject missing
  useEffect(() => {
    if (!boardId || !gradeId || !subjectId) {
      toast.error('Board/Grade/Subject missing. Please re-select from Papers.');
      navigate('/papers');
    }
  }, [boardId, gradeId, subjectId, navigate]);

  // Handle input change
  const handleChange = (e, key) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // ✅ Next button: update DB first, then navigate
  const handleNext = async (e) => {
    e.preventDefault();
    const anyCount = Object.values(formData).some(v => v && Number(v) > 0);
    if (!anyCount) {
      toast.error('Please enter at least one question count.');
      return;
    }

    localStorage.setItem(PAPER_FORM_KEY, JSON.stringify(formData));

    try {
      const token = localStorage.getItem('token');

      // Update counts in DB
      await axios.put(
        `${apiBase}/papers/${paperId}/update-counts`,
        {
          mcq: Number(formData.mcq || 0),
          fill_in_the_blanks: Number(formData.fill || 0),
          true_false: Number(formData.truefalse || 0),
          very_short_qa: Number(formData.veryshort || 0),
          short_qa: Number(formData.short || 0),
          long_qa: Number(formData.long || 0),
          matching_type: Number(formData.matching || 0),
          diagram_image_based: Number(formData.diagram || 0),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Counts saved successfully!');

      // Navigate to Regularpaperedit with latest counts
      navigate(`/regularpaperedit/${paperId}`, {
        state: { boardId, gradeId, subjectId, counts: formData, selectedQuestions, paperId },
      });

    } catch (err) {
      console.error('Failed to save counts:', err);
      toast.error('Failed to save counts. Please try again.');
    }
  };

  
  // ------------------- Profile Modal Stuff -------------------
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleDropdown = () => setShowDropdown(prev => !prev);

  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });

  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });

  // Fetch dashboard & user data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${apiBase}/getDashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || '',
              email: res.data.user.email || '',
              password: '',
            });
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error.response?.data || error);
      }
    };
    fetchDashboardData();
  }, []);

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully!');
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error('Error updating profile:', error.response?.data || error);
      toast.error('Failed to update profile');
    }
  };

  // ------------------- JSX -------------------
  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />
      <main className="main-content">
        <div className="select-header">
          <div className="papers-welcome-text">
            <a
              onClick={() =>
                navigate(`/papersedit/${paperId}`, { state: { boardId, gradeId, subjectId } })
              }
              className="back-link"
            >
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="select-welcome-text">
              Regular Paper - Enter Counts
            </span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className="edituser" onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleNext} className="question-form-container">
          <h3>Enter Number Of Questions</h3>
          <div className="question-grid">
            {[
              { key: 'mcq', label: 'M.C.Q ', placeholder: 'Enter M.C.Qs' },
              { key: 'fill', label: 'Fill in the blank', placeholder: 'Enter Fill in the blank' },
              { key: 'truefalse', label: 'True - False', placeholder: 'Enter True - False' },
              { key: 'veryshort', label: 'Very Short Answer Questions', placeholder: 'Enter Very Short Answer Questions' },
              { key: 'short', label: 'Short Answer Questions', placeholder: 'Enter Short Answer Questions' },
              { key: 'long', label: 'Long Answer Questions/Essay Questions', placeholder: 'Enter Long Answer Questions' },
              { key: 'matching', label: 'Matching Type', placeholder: 'Enter Matching Type' },
              { key: 'diagram', label: 'Diagram-based/Image-based Questions', placeholder: 'Enter Diagram-based Questions' },
            ].map(item => (
              <div key={item.key} className="form-item">
                <label><span className="dot">●</span> {item.label}</label>
                <input
                  type="text"
                  placeholder={item.placeholder}
                  value={formData[item.key] || ''}
                  onChange={e => handleChange(e, item.key)}
                />
              </div>
            ))}
          </div>
          <div className="next-button-container">
            <button type="submit" className="next-btn">Next</button>
          </div>
        </form>
      </main>

      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default SelectRegularPaperedit;
